import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import {
  HpiDataTable,
  HpiFilterSelect,
  HpiPagination,
  HpiStatsBar,
} from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

/* -- Types ------------------------------------------------ */

interface ClientTrendRow {
  id: number
  name: string | null
  rep: string | null
  type: string | null
  cases_3mo: number
  cases_6mo: number
  cases_1yr: number
  change_6mo: number | null
  ref_y1: number
  ref_y2: number
  ref_y3: number
  total_3yr: number
  year1: number
  year2: number
  year3: number
}

interface SelectOption {
  value: string | number
  label: string
}

interface Props {
  results: ClientTrendRow[]
  customer_types: SelectOption[]
  categories: SelectOption[]
  states: SelectOption[]
  selected_type_id: string
  selected_category_id: string
  selected_state: string
}

/* -- Constants -------------------------------------------- */

const PER_PAGE = 25

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Reports", href: "/reports/case_activity" },
  { title: "Client Trends", href: "/reports/marketing/client_trends" },
]

/* -- Columns ---------------------------------------------- */

const YELLOW_BG = "bg-[#F5F5E0]"

function buildColumns(
  year1: number,
  year2: number,
  year3: number,
): HpiColumn<ClientTrendRow>[] {
  return [
    {
      header: "Customer",
      render: (row) => (
        <a
          href={`/customers/${row.id}`}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-blue-600 hover:underline"
        >
          {row.name ?? "-"}
        </a>
      ),
    },
    {
      header: "Rep",
      width: "max-w-[120px]",
      render: (row) => <span className="text-sm">{row.rep ?? ""}</span>,
    },
    {
      header: "Type",
      width: "max-w-[100px]",
      render: (row) => <span className="text-sm">{row.type ?? ""}</span>,
    },
    {
      header: "3mo",
      width: "max-w-[60px]",
      render: (row) => (
        <span className="block text-right text-sm">{row.cases_3mo || ""}</span>
      ),
    },
    {
      header: "6mo",
      width: "max-w-[60px]",
      render: (row) => (
        <span className="block text-right text-sm">{row.cases_6mo || ""}</span>
      ),
    },
    {
      header: "1yr",
      width: "max-w-[60px]",
      render: (row) => (
        <span className="block text-right text-sm">{row.cases_1yr || ""}</span>
      ),
    },
    {
      header: "6mo Chg",
      width: "max-w-[80px]",
      cellClassName: YELLOW_BG,
      render: (row) => {
        if (row.change_6mo === null) return <span className="text-sm">-</span>
        const color =
          row.change_6mo > 0
            ? "text-green-700"
            : row.change_6mo < 0
              ? "text-red-600"
              : ""
        return (
          <span className={`block text-right text-sm font-medium ${color}`}>
            {row.change_6mo > 0 ? "+" : ""}
            {row.change_6mo}%
          </span>
        )
      },
    },
    {
      header: String(year1),
      width: "max-w-[60px]",
      cellClassName: YELLOW_BG,
      render: (row) => (
        <span className="block text-right text-sm">{row.ref_y1 || ""}</span>
      ),
    },
    {
      header: String(year2),
      width: "max-w-[60px]",
      cellClassName: YELLOW_BG,
      render: (row) => (
        <span className="block text-right text-sm">{row.ref_y2 || ""}</span>
      ),
    },
    {
      header: String(year3),
      width: "max-w-[60px]",
      cellClassName: YELLOW_BG,
      render: (row) => (
        <span className="block text-right text-sm">{row.ref_y3 || ""}</span>
      ),
    },
  ]
}

/* -- Main Component --------------------------------------- */

export default function ClientTrendsReport({
  results,
  customer_types,
  categories,
  states,
  selected_type_id,
  selected_category_id,
  selected_state,
}: Props) {
  const [typeId, setTypeId] = useState(selected_type_id)
  const [categoryId, setCategoryId] = useState(selected_category_id)
  const [stateFilter, setStateFilter] = useState(selected_state)
  const [page, setPage] = useState(1)

  const totalPages = Math.ceil(results.length / PER_PAGE)
  const paginated = results.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const currentYear =
    results.length > 0 ? results[0].year1 : new Date().getFullYear()
  const columns = buildColumns(currentYear, currentYear - 1, currentYear - 2)

  const handleUpdate = () => {
    setPage(1)
    router.get(
      "/reports/marketing/client_trends",
      {
        type_id: typeId || undefined,
        category_id: categoryId || undefined,
        state: stateFilter || undefined,
      },
      { preserveState: true },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Client Trends" />

      <div className="p-6">
        {/* Filter Bar */}
        <div className="bg-hpi-pale-gray mb-6 flex flex-wrap items-end gap-4 rounded-lg px-4 py-3">
          <HpiFilterSelect
            label="Type"
            value={typeId}
            onChange={setTypeId}
            options={customer_types}
          />
          <HpiFilterSelect
            label="Category"
            value={categoryId}
            onChange={setCategoryId}
            options={categories}
          />
          <HpiFilterSelect
            label="State"
            value={stateFilter}
            onChange={setStateFilter}
            options={states}
          />
          <button
            onClick={handleUpdate}
            className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-dark-blue h-8 rounded border px-5 text-sm font-medium transition-colors hover:text-white"
          >
            Search
          </button>
        </div>

        {/* Data Table */}
        <HpiDataTable<ClientTrendRow>
          title="Client Trends"
          columns={columns}
          data={paginated}
          keyFn={(row) => row.id}
          pagination={
            results.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={results.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />

        {/* Summary Stats */}
        <HpiStatsBar
          className="mt-6"
          stats={[
            {
              label: "Total",
              subLabel: "Customers",
              value: results.length,
            },
          ]}
        />
      </div>
    </AppLayout>
  )
}
